import { reportsPosition } from '../GscTools/metrics';
import { REPORTING_TIMEZONE, formatPeriod, searchTypeLabel } from '../GscTools/period';
import { MATCH_POLICY, segmentLabel } from './brand';

/**
 * The CSV of the segment comparison: the two sides of the rule, and the rule that made them.
 *
 * The definition travels in the file's header, as the terms that were typed and as the single
 * expression they compiled to. A brand number without its rule beside it is not a measurement of
 * anything — two people with different term lists would produce different files under the same
 * heading — so the two are never separated.
 *
 * The share's denominator is named in the header as well as in its column, because a share is the
 * one figure here that a reader will otherwise assume is out of the property's total traffic. A
 * metric Search Console did not return stays empty rather than becoming a 0.
 */
export function buildBrandCsv({ t, scope, rows, fetchedAt }) {
    const hasPosition = reportsPosition(scope.searchType);
    const literal = scope.policy === MATCH_POLICY.LITERAL;

    const meta = [
        [t('tools.brand_non_brand.export.report'), t('tools.brand_non_brand.header')],
        [t('tools.gsc.exports.property'), scope.siteUrl],
        [
            t('tools.brand_non_brand.export.policy'),
            t(`tools.brand_non_brand.policies.${scope.policy}`),
        ],
        [
            t('tools.brand_non_brand.export.terms'),
            literal ? scope.terms.map((term) => term.match).join(' | ') : '',
        ],
        [t('tools.brand_non_brand.export.pattern'), scope.compiledPattern ?? ''],
        [t('tools.brand_non_brand.export.brand_filter'), 'query includingRegex'],
        [t('tools.brand_non_brand.export.non_brand_filter'), 'query excludingRegex'],
        [t('tools.gsc.coverage.current'), formatPeriod(scope.current)],
        [t('tools.gsc.coverage.baseline'), scope.baseline ? formatPeriod(scope.baseline) : ''],
        [t('tools.gsc.controls.search_type'), searchTypeLabel(scope.searchType, t)],
        [t('tools.gsc.controls.country'), scope.country || 'all'],
        [t('tools.gsc.controls.device'), scope.device || 'all'],
        [t('tools.gsc.exports.data_state'), t(`tools.gsc.data_state.${scope.dataState}`)],
        [t('tools.gsc.exports.timezone'), REPORTING_TIMEZONE],
        [
            t('tools.brand_non_brand.export.denominator'),
            t('tools.brand_non_brand.coverage.denominator_value'),
        ],
        [
            t('tools.brand_non_brand.export.coverage'),
            t('tools.brand_non_brand.coverage.unknown_value'),
        ],
        [t('tools.gsc.exports.fetched_at'), fetchedAt ?? ''],
        [],
    ];

    const header = [
        t('tools.brand_non_brand.comparison.segment'),
        t('tools.brand_non_brand.export.filter_operator'),
        t('tools.gsc.columns.current_clicks'),
        t('tools.gsc.columns.prior_clicks'),
        t('tools.gsc.columns.delta_clicks'),
        t('tools.gsc.columns.delta_percent'),
        t('tools.gsc.columns.current_impressions'),
        t('tools.gsc.columns.prior_impressions'),
        t('tools.gsc.columns.current_ctr'),
        t('tools.gsc.columns.prior_ctr'),
        ...(hasPosition
            ? [t('tools.gsc.columns.current_position'), t('tools.gsc.columns.prior_position')]
            : []),
        t('tools.brand_non_brand.comparison.share'),
    ];

    const body = rows.map((row) => [
        segmentLabel(t, row.key),
        row.operator,
        row.current?.clicks,
        row.prior?.clicks,
        row.deltaClicks,
        row.deltaPercent,
        row.current?.impressions,
        row.prior?.impressions,
        row.current?.ctr,
        row.prior?.ctr,
        ...(hasPosition ? [row.current?.position, row.prior?.position] : []),
        row.share,
    ]);

    return [...meta, header, ...body];
}
